import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Loader2, Search } from 'lucide-react';
import { CaseCptLine, SurgeonTemplate } from '@/lib/templates/types';
import { getSecondaryDefaultModifiers } from '@/lib/modifiers/xepsu';
import { useCptSearch } from '@/hooks/useCptSearch';
import type { CptSearchEntry } from '@/lib/cptSearch/catalog';

interface AddProcedureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (line: CaseCptLine) => void;
  existingCodes: Set<string>;
  cptLines?: CaseCptLine[];
  template?: SurgeonTemplate | null;
}

/** Add-on CPT codes are typically in the 20000-69999 range with specific patterns,
 *  but the most reliable indicator is the category field from our database. */
function isAddOnCode(result: CptSearchEntry): boolean {
  const cat = (result.category || '').toLowerCase();
  return cat.includes('add-on') || cat.includes('addon') || cat === 'add_on';
}

function sourceLabel(result: CptSearchEntry): string {
  if (result.preAuthorized) return 'Authorized';
  if (result.plausible) return 'Plausible';
  if (result.source === 'cpt_catalog') return 'Catalog';
  if (result.source === 'procedure') return 'Procedure';
  return result.source.replace('_', ' ');
}

export function AddProcedureDialog({
  open,
  onOpenChange,
  onAdd,
  existingCodes,
  cptLines,
  template,
}: AddProcedureDialogProps) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<CptSearchEntry | null>(null);
  const [section, setSection] = useState<'primary' | 'secondary'>('secondary');
  const { results, isSearching } = useCptSearch(query, {
    authorizedLines: cptLines,
    template,
    primaryCpt: cptLines?.[0]?.code,
    existingCodes,
    limit: 20,
  });

  const handleAdd = () => {
    if (!selected) return;
    onAdd({
      code: selected.code,
      description: selected.description,
      modifiers: section === 'secondary' ? getSecondaryDefaultModifiers() : [],
      section,
      fromTemplate: false,
    });
    setQuery('');
    setSelected(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Procedure</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search CPT code or description…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              className="pl-10"
              autoFocus
            />
            {isSearching && (
              <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
            )}
          </div>

          <div className="max-h-48 overflow-y-auto space-y-1">
            {isSearching && <p className="text-xs text-muted-foreground text-center py-4">Searching…</p>}
            {!isSearching && results.length === 0 && query.length >= 2 && (
              <p className="text-xs text-muted-foreground text-center py-4">No results</p>
            )}
            {results.map(r => (
              <div
                key={r.code}
                className={`p-2 rounded-md cursor-pointer text-sm ${
                  selected?.code === r.code ? 'bg-primary/10 border border-primary/30' : 'hover:bg-accent/50'
                }`}
                onClick={() => setSelected(r)}
              >
                <div className="flex items-center gap-1.5">
                  <span className="font-mono font-medium">{r.code}</span>
                  <Badge variant="outline" className="text-[9px] h-4 border-primary/20 text-primary">
                    {sourceLabel(r)}
                  </Badge>
                  {isAddOnCode(r) && (
                    <Badge variant="outline" className="text-[9px] h-4 border-amber-500/50 text-amber-600 dark:text-amber-400">
                      Add-on
                    </Badge>
                  )}
                </div>
                <span className="text-muted-foreground">{r.description}</span>
              </div>
            ))}
          </div>

          {selected && (
            <div className="space-y-2">
              <Label className="text-xs font-medium">Insert as:</Label>
              <RadioGroup value={section} onValueChange={v => setSection(v as 'primary' | 'secondary')}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="primary" id="primary" />
                  <Label htmlFor="primary" className="text-sm">Primary Procedure</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="secondary" id="secondary" />
                  <Label htmlFor="secondary" className="text-sm">Secondary (XS distinct-structure default)</Label>
                </div>
              </RadioGroup>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleAdd} disabled={!selected}>Add to Case</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
